import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ArticleCardComponent, ArticleCardData } from '../article-card/article-card.component';
import { BlogListingService } from '../../services/blog-listing.service';
import { AudienceService } from '../../services/audience.service';
import { environment } from '../../../environments/environment';
import { getTaxonomyConfig, TaxonomyType } from '../../config/taxonomy.config';
import { dedupeArticleCardsByIdOrUrl } from '../../utils/blog-published-date';

/**
 * Category item for display
 */
interface CategoryItem {
  id: string;
  name: string;
  articleCount: number;
}

/**
 * BrowseByCategoryComponent - Homepage category browser with preview
 * 
 * Features:
 * - Displays vertical list of categories (topics, subjects, disciplines, etc.)
 * - Shows 3 latest articles for selected category
 * - "View All" link to full listing page
 * - Responsive: sidebar on desktop, stacked on mobile
 * - Auto-filters categories with no articles (NEW!)
 * - Simple exclusion filtering (NEW!)
 * 
 * Usage in Builder.io:
 * 1. Drag component onto homepage
 * 2. Select categoryType (topic, subject, discipline, content-series)
 * 3. Exclude unwanted categories (optional):
 *    - Leave empty = Show ALL categories with articles
 *    - Select one or more = Hide those, show everything else
 *    - Example: Exclude "Admin" and "Internal" topics → Shows all other topics
 * 4. Configure display options (sortBy, previewCount, etc.)
 * 5. Publish
 * 
 * Simple Rules:
 * - No categories excluded → Show all categories that have articles
 * - Categories excluded → Hide those, show everything else
 * - Categories with no articles → Automatically hidden
 * - First category → Auto-selected on page load
 * 
 * Why Exclusion instead of Inclusion?
 * - Much faster to exclude 2-3 unwanted categories than select 15-17 wanted ones
 * - Better UX: "Hide these few" vs "Show these many"
 * 
 * Reference Fields:
 * Multi-select exclusion lists (one appears based on categoryType):
 * - excludedTopics: Topics to hide (empty = show all)
 * - excludedContentSeries: Content series to hide (empty = show all)
 * - excludedSubjects: Subjects to hide (empty = show all)
 * - excludedDisciplines: Disciplines to hide (empty = show all)
 */
@Component({
  selector: 'browse-by-category',
  templateUrl: './browse-by-category.component.html',
  styleUrls: ['./browse-by-category.component.scss'],
  standalone: false
})
export class BrowseByCategoryComponent implements OnInit {
  
  // ═══════════════════════════════════════════════════════
  // BUILDER.IO CONFIGURABLE INPUTS
  // ═══════════════════════════════════════════════════════
  
  @Input() categoryType: TaxonomyType = 'topic';
  
  // Multi-select category lists for EXCLUSION (NEW! - Exclude specific categories)
  // Empty = Show All, Has values = Exclude those (show everything else)
  @Input() excludedTopics?: any[];          // Topics to hide (empty = show all)
  @Input() excludedContentSeries?: any[];   // Content series to hide (empty = show all)
  @Input() excludedSubjects?: any[];        // Subjects to hide (empty = show all)
  @Input() excludedDisciplines?: any[];     // Disciplines to hide (empty = show all)
  
  @Input() previewCount: number = 3;  // Total articles to fetch (pool size)
  @Input() basePath: string = '/content-hub';
  @Input() sortBy: 'alphabetical' | 'articleCount' = 'alphabetical';
  @Input() viewAllUrlOverrides?: Record<string, string>; // Custom URL mappings for specific categories
  @Input() fallbackImages: string[] = [];
  @Input() showFeaturedImageFallback: boolean = true;
  
  // Fixed display count (always show 3 cards on screen)
  private readonly DISPLAY_COUNT = 3;

  /**
   * Builder returns at most this many rows per request. We intentionally request
   * more rows than {@link previewCount} so the true newest posts are in the
   * candidate set before client-side date sort; asking for only previewCount (e.g. 3)
   * can omit newer posts if server ordering does not match real publish dates.
   */
  private readonly BROWSE_API_FETCH_CAP = 100;
  
  // ═══════════════════════════════════════════════════════
  // COMPONENT STATE
  // ═══════════════════════════════════════════════════════
  
  categories: CategoryItem[] = [];
  selectedCategory: CategoryItem | null = null;
  previewArticles: ArticleCardData[] = [];
  allArticlesForCategory: ArticleCardData[] = []; // All articles for current category
  currentArticleOffset: number = 0; // Current offset for article pagination
  loadingCategories: boolean = false;
  loadingArticles: boolean = false;
  error: string | null = null;
  
  // Dynamically fetched fallback images from Builder.io config
  // Store as objects to preserve both URL and altText metadata  
  private dynamicFallbackImages: Array<{ url: string; altText?: string }> = [];
  
  // PERFORMANCE: Cache article results per category
  private articleCache = new Map<string, ArticleCardData[]>();
  
  // PERFORMANCE: Cache keys and TTL constants
  private readonly CATEGORY_VALIDATION_CACHE_KEY = 'browse-category-validation';
  private readonly FALLBACK_IMAGES_CACHE_KEY = 'browse-fallback-images';
  private readonly CATEGORY_VALIDATION_TTL = 10 * 60 * 1000; // 10 minutes
  private readonly FALLBACK_IMAGES_TTL = 60 * 60 * 1000; // 1 hour
  
  // ═══════════════════════════════════════════════════════
  // COMPUTED PROPERTIES
  // ═══════════════════════════════════════════════════════
  
  get config() {
    return getTaxonomyConfig(this.categoryType);
  }
  
  get sectionTitle(): string {
    return `Browse By ${this.config.label}`;
  }
  
  get viewAllUrl(): string {
    if (!this.selectedCategory) return '#';
    
    // Check for custom URL override first
    if (this.viewAllUrlOverrides && this.viewAllUrlOverrides[this.selectedCategory.name]) {
      return this.viewAllUrlOverrides[this.selectedCategory.name];
    }
    
    // Generate URL dynamically
    const audience = this.audienceService.audience;
    const categoryName = this.selectedCategory.name;
    const pluralPath = this.config.pluralLabel.toLowerCase().replace(/\s+/g, '-');
    
    return `${this.basePath}/${audience}/${pluralPath}?${this.config.queryParamName}=${encodeURIComponent(categoryName)}`;
  }
  
  get canNavigatePrev(): boolean {
    return this.currentArticleOffset > 0;
  }
  
  get canNavigateNext(): boolean {
    return this.currentArticleOffset + this.DISPLAY_COUNT < this.allArticlesForCategory.length;
  }
  
  get showNavigationArrows(): boolean {
    return true;  // Always show arrows (they'll be greyed out when disabled)
  }
  
  // ═══════════════════════════════════════════════════════
  // CONSTRUCTOR & LIFECYCLE
  // ═══════════════════════════════════════════════════════
  
  constructor(
    private blogService: BlogListingService,
    private audienceService: AudienceService
  ) {}
  
  async ngOnInit() {
    // Initialize fallback images
    await this.loadFallbackImagesFromFolder();
    
    if (this.dynamicFallbackImages.length === 0 && (!this.fallbackImages || this.fallbackImages.length === 0)) {
      this.fallbackImages = this.getDefaultFallbackImages();
    }
    
    await this.loadCategories();
    
    // Auto-select first category if categories loaded
    if (this.categories.length > 0) {
      await this.selectCategory(this.categories[0]);
    }
  }
  
  // ═══════════════════════════════════════════════════════
  // DATA LOADING - CATEGORIES
  // ═══════════════════════════════════════════════════════
  
  /**
   * Load categories directly from Builder.io taxonomy models
   * NEW: Validates categories have articles and filters out empty ones
   */
  async loadCategories(): Promise<void> {
    this.loadingCategories = true;
    this.error = null;
    
    try {
      // Fetch categories from Builder.io taxonomy model
      const categoryEntries = await this.fetchCategoriesFromModel();
      
      // Map to CategoryItem
      const allCategories = this.mapCategoriesToItems(categoryEntries);
      
      // Apply exclusion filter: if categories excluded, hide those; else show all
      const filteredCategories = this.applyExclusionFilter(allCategories);
      
      // Sort
      const sortedCategories = this.sortCategories(filteredCategories);
      
      // Verify which categories have articles (filter out empty ones)
      const categoriesWithArticles = await this.filterCategoriesWithArticles(sortedCategories);
      
      this.categories = categoriesWithArticles;
      
    } catch (error) {
      this.error = 'Failed to load categories';
      this.categories = [];
    } finally {
      this.loadingCategories = false;
    }
  }
  
  /**
   * Fetch category entries from Builder.io taxonomy model
   */
  private async fetchCategoriesFromModel(): Promise<any[]> {
    const apiKey = environment.builderApiKey;
    const modelName = this.config.modelName;
    const url = `https://cdn.builder.io/api/v3/content/${modelName}?apiKey=${apiKey}&limit=100&fields=id,data.name,data.coursename`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      return [];
    }
  }
  
  /**
   * Map category entries to CategoryItem objects
   */
  private mapCategoriesToItems(categoryEntries: any[]): CategoryItem[] {
    const nameField = this.config.nameField || 'name';
    const categories: CategoryItem[] = [];
    
    for (const entry of categoryEntries) {
      const categoryData = entry.data || {};
      const categoryName = categoryData[nameField] || categoryData.name;
      
      if (categoryName) {
        categories.push({
          id: entry.id,
          name: categoryName,
          articleCount: 0 // Will be verified
        });
      }
    }
    
    return categories;
  }
  
  /**
   * Filter out categories that have no articles
   * PERFORMANCE: Uses localStorage cache with 10-minute TTL to avoid repeated validation API calls
   */
  private async filterCategoriesWithArticles(categories: CategoryItem[]): Promise<CategoryItem[]> {
    // Try to get cached validation results
    const cachedValidation = this.getCachedCategoryValidation();
    
    if (cachedValidation) {
      // Filter categories based on cached validation
      const validatedCategories = categories.filter(cat => cachedValidation.validCategoryIds.has(cat.id));
      return validatedCategories;
    }
    
    // No cache or expired - perform validation
    const validationPromises = categories.map(async (category) => {
      try {
        const result = await this.blogService.fetchBlogs({
          mode: this.categoryType as any,
          filterId: category.id,
          filterValue: category.name,
          limit: 1, // Only need to check if at least 1 exists
          offset: 0,
          sortOrder: 'newest'
        });
        
        const hasArticles = result.results.length > 0;
        return hasArticles ? category : null;
      } catch (error) {
        return null; // Exclude if error
      }
    });
    
    const validatedCategories = await Promise.all(validationPromises);
    const categoriesWithArticles = validatedCategories.filter(cat => cat !== null) as CategoryItem[];
    
    // Cache the validation results
    const validCategoryIds = new Set(categoriesWithArticles.map(cat => cat.id));
    this.setCachedCategoryValidation(validCategoryIds);
    
    return categoriesWithArticles;
  }
  
  /**
   * Get the excluded categories list based on categoryType
   */
  private getExcludedCategoriesList(): any[] {
    switch (this.categoryType) {
      case 'topic':
        return this.excludedTopics || [];
      case 'content-series':
        return this.excludedContentSeries || [];
      case 'subject':
        return this.excludedSubjects || [];
      case 'discipline':
        return this.excludedDisciplines || [];
      default:
        return [];
    }
  }
  
  /**
   * Apply exclusion filter: Hide excluded categories, show everything else
   * SIMPLE RULE: Empty list = Show All, Has values = Exclude those
   */
  private applyExclusionFilter(categories: CategoryItem[]): CategoryItem[] {
    const excludedList = this.getExcludedCategoriesList();
    
    // If no categories excluded, show all
    if (excludedList.length === 0) {
      return categories;
    }
    
    // Extract IDs from excluded list
    // Builder.io list format: [{ topic: refObject }, { topic: refObject }, ...]
    const excludedIds = new Set<string>();
    const fieldNameMap: Record<string, string> = {
      'topic': 'topic',
      'content-series': 'series',
      'subject': 'subject',
      'discipline': 'discipline'
    };
    const fieldName = fieldNameMap[this.categoryType];
    
    excludedList.forEach(item => {
      // Handle different Builder.io structures
      let refObject = item;
      
      // If item is wrapped (e.g., { topic: refObject })
      if (item && typeof item === 'object' && fieldName && item[fieldName]) {
        refObject = item[fieldName];
      }
      
      // Extract ID from reference object
      if (typeof refObject === 'string') {
        excludedIds.add(refObject);
      } else if (refObject && typeof refObject === 'object') {
        const id = refObject.id || refObject.value?.id || refObject.value?.data?.id;
        if (id) excludedIds.add(id);
      }
    });
    
    // Show all EXCEPT the excluded categories
    const filtered = categories.filter(cat => !excludedIds.has(cat.id));
    
    return filtered;
  }
  
  /**
   * Sort categories based on configuration
   */
  private sortCategories(categories: CategoryItem[]): CategoryItem[] {
    return [...categories].sort((a, b) => {
      if (this.sortBy === 'alphabetical') {
        return a.name.localeCompare(b.name);
      } else {
        // Sort by article count (descending)
        return b.articleCount - a.articleCount;
      }
    });
  }
  
  // ═══════════════════════════════════════════════════════
  // CATEGORY SELECTION
  // ═══════════════════════════════════════════════════════
  
  
  /**
   * User clicks on a category - load its preview articles
   * PERFORMANCE: Uses cache to avoid re-fetching
   */
  async selectCategory(category: CategoryItem): Promise<void> {
    // Don't reload if already selected
    if (this.selectedCategory?.id === category.id) {
      return;
    }
    
    this.selectedCategory = category;
    this.currentArticleOffset = 0; // Reset offset when changing category
    
    // Check cache first
    if (this.articleCache.has(category.id)) {
      this.allArticlesForCategory = this.articleCache.get(category.id)!;
      this.updateDisplayedArticles();
      return;
    }
    
    this.loadingArticles = true;
    this.error = null;
    
    try {
      const poolSize = Math.max(1, Number(this.previewCount) || this.DISPLAY_COUNT);
      // Fetch a wide candidate set, then keep only the top poolSize after sort (in service).
      const fetchLimit = Math.min(
        this.BROWSE_API_FETCH_CAP,
        Math.max(poolSize, 50)
      );

      const result = await this.blogService.fetchBlogs({
        mode: this.categoryType as any,
        filterId: category.id,
        filterValue: category.name,
        limit: fetchLimit,
        offset: 0,
        sortOrder: 'newest'
      });

      const topForPool = result.results.slice(0, poolSize);

      // Map to card data; collapse duplicates (same slug or id can still surface)
      this.allArticlesForCategory = dedupeArticleCardsByIdOrUrl(
        topForPool.map((article, index) => this.mapArticleToCard(article, index))
      );

      // Cache the results
      this.articleCache.set(category.id, this.allArticlesForCategory);
      
      // Update displayed articles (always shows DISPLAY_COUNT = 3 cards)
      this.updateDisplayedArticles();
      
    } catch (error) {
      this.error = 'Failed to load articles';
      this.allArticlesForCategory = [];
      this.previewArticles = [];
    } finally {
      this.loadingArticles = false;
    }
  }
  
  /**
   * Update displayed articles based on current offset
   * Always shows DISPLAY_COUNT (3) cards on screen
   */
  private updateDisplayedArticles(): void {
    const endIndex = this.currentArticleOffset + this.DISPLAY_COUNT;
    this.previewArticles = this.allArticlesForCategory.slice(this.currentArticleOffset, endIndex);
  }
  
  /**
   * Navigate to previous article (shift left by 1)
   */
  navigatePrev(): void {
    if (this.canNavigatePrev) {
      this.currentArticleOffset--;
      this.updateDisplayedArticles();
    }
  }
  
  /**
   * Navigate to next article (shift right by 1)
   */
  navigateNext(): void {
    if (this.canNavigateNext) {
      this.currentArticleOffset++;
      this.updateDisplayedArticles();
    }
  }
  
  // ═══════════════════════════════════════════════════════
  // ARTICLE MAPPING (Reused from ArticleListingComponent)
  // ═══════════════════════════════════════════════════════
  
  /**
   * Map Builder.io article to card display data
   * Reuses same logic as ArticleListingComponent for consistency
   */
  private mapArticleToCard(article: any, index: number): ArticleCardData {
    try {
      const data = article?.data || {};
      
      // Get featured image with fallback and alt text
      const imageResult = this.getFeaturedImageWithAlt(data, article.id, index);
      
      // Get author data
      const author = {
        name: data.authors?.value?.data?.name || 'Anonymous',
        avatarUrl: data.authors?.value?.data?.headshot || ''
      };
      
      // Build article URL
      const slug = data.slug || '';
      const audience = this.audienceService.audience;
      const prefix = slug.startsWith('/ref/') ? 'ref' : 
                     slug.startsWith('/post/') ? 'post' : 'ref';
      const cleanSlug = slug.replace(/^\/?(ref|post)\//, '');
      const url = `${this.basePath}/${audience}/${prefix}/${cleanSlug}`;
      
      // Strip HTML from excerpt
      const excerpt = this.stripHtml(data.excerpt || '');
      
      return {
        id: article.id || 'unknown',
        title: data.title || 'Untitled',
        excerpt: excerpt,
        imageUrl: imageResult.url,
        imageAlt: imageResult.alt,  // ✅ Now includes alt text!
        publishedDate: data.publishedDate || '',
        author: author,
        url: url
      };
    } catch (error) {
      return {
        id: article?.id || 'error',
        title: 'Error loading article',
        excerpt: '',
        imageUrl: 'https://placehold.co/600x400?text=Error',
        imageAlt: 'Error loading article image',  // ✅ Error alt text
        publishedDate: '',
        author: { name: 'Unknown', avatarUrl: '' },
        url: '#'
      };
    }
  }
  
  /**
   * Get featured image with fallback logic and rotation
   * 
   * NEW DATA STRUCTURE (v1.1.1):
   * - featuredImage is now an Object with nested 'altImage' (File type)
   * - altImage contains: { url: string, altText: string, ... }
   * 
   * Priority:
   * 1. featuredImage.altImage (new nested structure)
   * 2. featuredImage direct file (legacy)
   * 3. Fallback rotation → Placeholder
   */
  private getFeaturedImage(data: any, index: number): string {
    const featured = data?.featuredImage;
    
    // 1. NEW STRUCTURE: Check for nested altImage (Object with File field)
    if (featured && typeof featured === 'object' && featured.altImage) {
      const altImage = featured.altImage;
      
      // altImage can be a direct URL string
      if (typeof altImage === 'string' && altImage.trim()) {
        return altImage;
      }
      
      // altImage can be an object with url property
      if (typeof altImage === 'object' && altImage.url) {
        const imageUrl = altImage.url;
        if (imageUrl && imageUrl.trim() !== '') {
          return imageUrl;
        }
      }
    }
    
    // 2. LEGACY: Try reference field
    if (featured?.value?.data?.image) {
      const imageUrl = featured.value.data.image;
      if (imageUrl && imageUrl.trim() !== '') {
        return imageUrl;
      }
    }
    
    // 3. LEGACY: Try direct URL
    if (featured?.url) {
      const imageUrl = featured.url;
      if (imageUrl && imageUrl.trim() !== '') {
        return imageUrl;
      }
    }
    
    // 4. LEGACY: Try direct image field
    if (featured && typeof featured === 'string') {
      const imageUrl = featured.trim();
      if (imageUrl !== '') {
        return imageUrl;
      }
    }
    
    // 5. Fallback to rotated image if enabled
    if (this.showFeaturedImageFallback) {
      // Priority 1: Dynamic fallback images from Builder.io (with metadata extracted but altText not used here)
      if (this.dynamicFallbackImages && this.dynamicFallbackImages.length > 0) {
        const seed = this.selectedCategory?.id || 'default';
        const categoryHash = this.hashString(seed);
        const startIndex = Math.abs(categoryHash) % this.dynamicFallbackImages.length;
        const imageIndex = (startIndex + index) % this.dynamicFallbackImages.length;
        const fallbackItem = this.dynamicFallbackImages[imageIndex];
        return fallbackItem.url;  // Return URL (altText metadata available but not used)
      }
      
      // Priority 2: Input or hardcoded fallback images
      if (this.fallbackImages && this.fallbackImages.length > 0) {
        const seed = this.selectedCategory?.id || 'default';
        const categoryHash = this.hashString(seed);
        const startIndex = Math.abs(categoryHash) % this.fallbackImages.length;
        const imageIndex = (startIndex + index) % this.fallbackImages.length;
        const fallbackItem = this.fallbackImages[imageIndex];
        
        // Handle both string URLs (legacy/hardcoded) and objects with url/altText
        if (typeof fallbackItem === 'string') {
          return fallbackItem;
        } else if (fallbackItem && typeof fallbackItem === 'object') {
          const item = fallbackItem as { url: string; altText?: string };
          return item.url;
        }
      }
    }
    
    return 'https://placehold.co/600x400?text=No+Image';
  }
  
  /**
   * Get featured image with alt text for accessibility
   * Similar to article-listing component but adapted for browse-by-category
   * 
   * Priority:
   * 1. featuredImage.altImage with altText
   * 2. Fallback images from Builder.io (with altText)
   * 3. Hardcoded fallback images (generic altText)
   */
  private getFeaturedImageWithAlt(data: any, articleId?: string, index?: number): { url: string; alt: string } {
    const featured = data?.featuredImage;
    const articleTitle = data?.title || 'Article';
    
    // 1. NEW STRUCTURE: Check for nested altImage (Object with File field)
    if (featured && typeof featured === 'object' && featured.altImage) {
      const altImage = featured.altImage;
      const parentAltText = featured.altText;
      
      // altImage can be a direct URL string
      if (typeof altImage === 'string' && altImage.trim()) {
        return {
          url: altImage,
          alt: parentAltText || articleTitle
        };
      }
      
      // altImage can be an object with url and altText
      if (typeof altImage === 'object' && altImage.url) {
        const altText = altImage.altText || parentAltText || articleTitle;
        return {
          url: altImage.url,
          alt: altText
        };
      }
    }
    
    // 2. LEGACY: Check if featuredImage is a direct string URL
    if (typeof featured === 'string' && featured.trim()) {
      return {
        url: featured,
        alt: articleTitle
      };
    }
    
    // 3. LEGACY: Check if featuredImage is an object with 'url' property
    if (featured && typeof featured === 'object' && featured.url) {
      return {
        url: featured.url,
        alt: featured.altText || articleTitle
      };
    }
    
    // 4. LEGACY: Check if featuredImage is a reference field
    if (featured?.value?.data?.image) {
      return {
        url: featured.value.data.image,
        alt: featured.value.data.altText || articleTitle
      };
    }
    
    // 5. LEGACY: Check for nested url in value
    if (featured?.value?.url) {
      return {
        url: featured.value.url,
        alt: featured.value.altText || articleTitle
      };
    }
    
    // 6. Fallback to rotated image if enabled
    if (this.showFeaturedImageFallback) {
      // Priority 1: Dynamic fallback images from Builder.io (with altText!)
      if (this.dynamicFallbackImages && this.dynamicFallbackImages.length > 0 && typeof index === 'number') {
        const seed = this.selectedCategory?.id || 'default';
        const categoryHash = this.hashString(seed);
        const startIndex = Math.abs(categoryHash) % this.dynamicFallbackImages.length;
        const imageIndex = (startIndex + index) % this.dynamicFallbackImages.length;
        const fallbackItem = this.dynamicFallbackImages[imageIndex];
        return {
          url: fallbackItem.url,
          alt: fallbackItem.altText || 'Decorative image for article'  // ✅ Uses altText!
        };
      }
      
      // Priority 2: Hardcoded fallback images (no altText)
      if (this.fallbackImages && this.fallbackImages.length > 0 && typeof index === 'number') {
        const seed = this.selectedCategory?.id || 'default';
        const categoryHash = this.hashString(seed);
        const startIndex = Math.abs(categoryHash) % this.fallbackImages.length;
        const imageIndex = (startIndex + index) % this.fallbackImages.length;
        const fallbackItem = this.fallbackImages[imageIndex];
        
        if (typeof fallbackItem === 'string') {
          return {
            url: fallbackItem,
            alt: 'Decorative image for article'
          };
        } else if (fallbackItem && typeof fallbackItem === 'object') {
          const item = fallbackItem as { url: string; altText?: string };
          return {
            url: item.url,
            alt: item.altText || 'Decorative image for article'
          };
        }
      }
    }
    
    // 7. Last resort: placeholder
    return {
      url: 'https://placehold.co/600x400?text=No+Image',
      alt: 'Placeholder image - no featured image available'
    };
  }
  
  /**
   * Simple hash function for deterministic image rotation
   */
  private hashString(value: string): number {
    const v = String(value || '').trim();
    let hash = 0;
    for (let i = 0; i < v.length; i++) {
      hash = (hash * 31 + v.charCodeAt(i)) | 0;
    }
    return hash;
  }
  
  /**
   * Get default fallback images (hardcoded - used as last resort)
   * Returns simple string URLs (no altText for hardcoded images)
   */
  private getDefaultFallbackImages(): string[] {
    return [
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F4f7f666fd5f9483abb5e8bf66a69be24",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2Fc151390f9ea44861b1b4619154c84025",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F265b53f04d2c4f7a9c6839eae1efb7f9",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F074a34ef88bc4aa88950d62ee4dd091f",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F56383631a9ab415a9f1cfeb42abf132b",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F3f2252ffc95a4c1d854bdc7d210df290",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2Fc6fc4fa3f18742b887b07270cd8cb0f7",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F6c89f91bbbf2465d92733cae60a99525",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F78c3969726214512b450f749dc691913",
      "https://cdn.builder.io/api/v1/image/assets%2Fe8cf29fa74d849dbac75da3186e4fb4f%2F731c2ad7d4d1455db70112b5bc885afe"
    ];
  }
  
  /**
   * Load fallback images from Builder.io config
   * PERFORMANCE: Uses localStorage cache with 1-hour TTL to avoid repeated API calls
   */
  private async loadFallbackImagesFromFolder(): Promise<void> {
    // Try to get cached fallback images
    const cachedImages = this.getCachedFallbackImages();
    
    if (cachedImages && cachedImages.length > 0) {
      this.dynamicFallbackImages = cachedImages as any;  // Type coercion for cached data
      this.fallbackImages = cachedImages as any;
      return;
    }
    
    // No cache or expired - fetch from API
    try {
      const apiKey = environment.builderApiKey;
      const url = `https://cdn.builder.io/api/v3/content/fallback-images-config?apiKey=${apiKey}&query.id=986366fbe186497a8a70135c52243bdc&limit=1`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.results?.[0]?.data?.images) {
        this.dynamicFallbackImages = data.results[0].data.images
          .map((img: any) => {
            let url: string | null = null;
            let altText: string | undefined = undefined;
            
            // Handle direct string URL
            if (typeof img === 'string') {
              return { url: img, altText: undefined };
            }
            
            // Handle new structure: { image: { altImage: "url", altText: "text" } }
            if (img?.image) {
              // Extract altText from parent image object
              altText = img.image.altText;
              
              // Extract URL from altImage
              if (img.image.altImage) {
                // altImage can be a string URL
                if (typeof img.image.altImage === 'string') {
                  url = img.image.altImage;
                }
                // or an object with url property
                else if (typeof img.image.altImage === 'object' && img.image.altImage.url) {
                  url = img.image.altImage.url;
                  // Also check for nested altText
                  if (!altText && img.image.altImage.altText) {
                    altText = img.image.altImage.altText;
                  }
                }
              }
              // Legacy: { image: "url" }
              else if (typeof img.image === 'string') {
                url = img.image;
              }
            }
            
            // Legacy: { url: "url" }
            if (!url && typeof img?.url === 'string') {
              url = img.url;
              altText = img.altText;
            }
            
            return url ? { url, altText } : null;
          })
          .filter((item: any) => item !== null);
        
        if (this.dynamicFallbackImages.length > 0) {
          // Note: dynamicFallbackImages has altText metadata but fallbackImages (@Input) only expects URLs
          // For now, we extract metadata but browse-by-category only uses URLs
          // Cache the results with metadata
          this.setCachedFallbackImages(this.dynamicFallbackImages);
        }
      }
    } catch (error) {
      // Silently fail - will use hardcoded fallbacks
    }
  }
  
  /**
   * Strip HTML from Builder.io excerpt/rich text to plain text for safe display.
   * Do not assign remote HTML to innerHTML (DOM XSS); use string stripping only (browser + SSR).
   */
  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
  
  // ═══════════════════════════════════════════════════════
  // TEMPLATE HELPERS
  // ═══════════════════════════════════════════════════════
  
  /**
   * Track by function for ngFor performance
   */
  trackByCategoryId(index: number, category: CategoryItem): string {
    return category.id;
  }
  
  trackByArticleId(index: number, article: ArticleCardData): string {
    return article.id;
  }
  
  // ═══════════════════════════════════════════════════════
  // CACHE HELPERS (localStorage with TTL)
  // ═══════════════════════════════════════════════════════
  
  /**
   * Get cached category validation results
   * Returns Set of valid category IDs if cache is valid, null otherwise
   */
  private getCachedCategoryValidation(): { validCategoryIds: Set<string> } | null {
    try {
      const cacheKey = `${this.CATEGORY_VALIDATION_CACHE_KEY}-${this.categoryType}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - parsed.timestamp > this.CATEGORY_VALIDATION_TTL) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return {
        validCategoryIds: new Set(parsed.validCategoryIds)
      };
    } catch (error) {
      // localStorage might be disabled or full
      return null;
    }
  }
  
  /**
   * Cache category validation results
   */
  private setCachedCategoryValidation(validCategoryIds: Set<string>): void {
    try {
      const cacheKey = `${this.CATEGORY_VALIDATION_CACHE_KEY}-${this.categoryType}`;
      const cacheData = {
        validCategoryIds: Array.from(validCategoryIds),
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }
  
  /**
   * Get cached fallback images
   * Returns array of image objects/URLs if cache is valid, null otherwise
   */
  private getCachedFallbackImages(): Array<{ url: string; altText?: string }> | string[] | null {
    try {
      const cached = localStorage.getItem(this.FALLBACK_IMAGES_CACHE_KEY);
      
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - parsed.timestamp > this.FALLBACK_IMAGES_TTL) {
        localStorage.removeItem(this.FALLBACK_IMAGES_CACHE_KEY);
        return null;
      }
      
      return parsed.images;
    } catch (error) {
      // localStorage might be disabled or full
      return null;
    }
  }
  
  /**
   * Cache fallback images
   */
  private setCachedFallbackImages(images: Array<{ url: string; altText?: string }> | string[]): void {
    try {
      const cacheData = {
        images: images,
        timestamp: Date.now()
      };
      localStorage.setItem(this.FALLBACK_IMAGES_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }
}
